/**
 * 点数计算
 *
 * 立直麻将的点数体系：
 *   1. 基本点 = 符 × 2^(2+番)   （但番数 ≥ 5 时改用满贯公式）
 *   2. 按亲/子 × 自摸/荣和 换算实际支付点数
 *   3. 加上本场 bonus（每本场 +300 总）
 *   4. 进位到百位
 *
 * 满贯及以上（按番数）：
 *   5 番     = 满贯     基本点 2000
 *   6-7 番   = 跳满     基本点 3000
 *   8-10 番  = 倍满     基本点 4000
 *   11-12 番 = 三倍满   基本点 6000
 *   13 番+   = 累计役满 基本点 8000
 *   役满     = 基本点 8000 × 倍数
 *
 * 支付公式：
 *   亲家荣和   = 基本点 × 6    （进位到百位）
 *   亲家自摸   = 基本点 × 2 × 3（每家付 ×3）
 *   子家荣和   = 基本点 × 4
 *   子家自摸   = 基本点 × 2（庄家付）+ 基本点 × 1（闲家付）× 2
 */

import type { FuResult } from './fu.js';
import type { WinContext, YakuResult } from './yaku/types.js';

// ════════════════════════════════════════════════════════════════
// 类型
// ════════════════════════════════════════════════════════════════

/** 点数等级 */
export type ScoreRank =
  | 'normal' // 普通（按符番算）
  | 'mangan' // 满贯
  | 'haneman' // 跳满
  | 'baiman' // 倍满
  | 'sanbaiman' // 三倍满
  | 'yakuman'; // 役满

/** 支付明细 */
export interface Payment {
  /** 荣和时支付的点数（0 表示不适用） */
  readonly ron: number;
  /** 自摸时庄家支付的点数 */
  readonly tsumoDealer: number;
  /** 自摸时闲家支付的点数 */
  readonly tsumoNonDealer: number;
  /** 自摸总收入（含本场 bonus） */
  readonly tsumoTotal: number;
}

/** 点数计算结果 */
export interface ScoreResult {
  /** 基本点 */
  readonly basePoints: number;
  /** 等级 */
  readonly rank: ScoreRank;
  /** 亲家支付 */
  readonly dealer: Payment;
  /** 子家支付 */
  readonly nonDealer: Payment;
  /** 总番数 */
  readonly han: number;
  /** 符数 */
  readonly fu: number;
  /** 本场数 */
  readonly honba: number;
  /** 本场 bonus 总额 */
  readonly honbaBonus: number;
}

// ════════════════════════════════════════════════════════════════
// 主入口
// ════════════════════════════════════════════════════════════════

/** 满贯及以上的基本点 */
const RANK_BASE: Record<ScoreRank, number> = {
  normal: 0,
  mangan: 2000,
  haneman: 3000,
  baiman: 4000,
  sanbaiman: 6000,
  yakuman: 8000,
};

/**
 * 计算点数
 *
 * @param han 总番数（含宝牌）
 * @param fu 符数
 * @param ctx 场况（用 honba）
 * @param isDealer 是否亲家（自风 = 场风）
 * @param yakumanCount 役满倍数（0 表示非役满）
 */
export function calculateScore(
  han: number,
  fu: number,
  ctx: WinContext,
  isDealer: boolean,
  yakumanCount = 0,
): ScoreResult {
  const honba = ctx.honba ?? 0;
  const honbaBonus = honba * 300; // 每本场 +300 总

  // ── 确定等级与基本点 ──
  let rank: ScoreRank;
  let basePoints: number;

  if (yakumanCount > 0) {
    rank = 'yakuman';
    basePoints = RANK_BASE.yakuman * yakumanCount;
  } else if (han >= 13) {
    rank = 'yakuman'; // 累计役满
    basePoints = RANK_BASE.yakuman;
  } else if (han >= 11) {
    rank = 'sanbaiman';
    basePoints = RANK_BASE.sanbaiman;
  } else if (han >= 8) {
    rank = 'baiman';
    basePoints = RANK_BASE.baiman;
  } else if (han >= 6) {
    rank = 'haneman';
    basePoints = RANK_BASE.haneman;
  } else if (han >= 5) {
    rank = 'mangan';
    basePoints = RANK_BASE.mangan;
  } else {
    // 普通：基本点 = 符 × 2^(2+番)
    rank = 'normal';
    basePoints = fu * Math.pow(2, 2 + han);
    // 4 番 30 符以上、3 番 70 符以上 也按满贯
    if (basePoints > 2000) {
      rank = 'mangan';
      basePoints = RANK_BASE.mangan;
    }
  }

  // ── 亲家支付 ──
  const dealer: Payment = {
    ron: ceil100(basePoints * 6) + honbaBonus,
    tsumoDealer: ceil100(basePoints * 2),
    tsumoNonDealer: ceil100(basePoints * 2),
    tsumoTotal: ceil100(basePoints * 2) * 3 + honbaBonus,
  };

  // ── 子家支付 ──
  const nonDealer: Payment = {
    ron: ceil100(basePoints * 4) + honbaBonus,
    tsumoDealer: ceil100(basePoints * 2), // 庄家付
    tsumoNonDealer: ceil100(basePoints), // 闲家付
    tsumoTotal:
      ceil100(basePoints * 2) + ceil100(basePoints) * 2 + honbaBonus,
  };

  return {
    basePoints,
    rank,
    dealer,
    nonDealer,
    han,
    fu,
    honba,
    honbaBonus,
  };
}

/** 进位到百位 */
export function ceil100(n: number): number {
  return Math.ceil(n / 100) * 100;
}

// ════════════════════════════════════════════════════════════════
// 便捷入口（从役种结果 + 符数直接算）
// ════════════════════════════════════════════════════════════════

/**
 * 从役种结果 + 符数计算结果算点数
 */
export function scoreFrom(
  yakuResult: YakuResult,
  fuResult: FuResult,
  ctx: WinContext,
): ScoreResult {
  // 庄家 = 自风为东者（dealerSeat 决定，随局数轮换）
  // ⚠️ 旧实现 `seatWind === roundWind`：仅在东风战成立，南场会把庄家判成非庄家（点数错）。
  const isDealer = ctx.seatWind === 'east';
  const han = yakuResult.hasYakuman ? 0 : yakuResult.totalHan;
  const fu = yakuResult.hasYakuman ? 0 : fuResult.fu;
  return calculateScore(han, fu, ctx, isDealer, yakuResult.yakumanCount);
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 等级中文名 */
export function rankName(r: ScoreRank): string {
  const names: Record<ScoreRank, string> = {
    normal: '',
    mangan: '满贯',
    haneman: '跳满',
    baiman: '倍满',
    sanbaiman: '三倍满',
    yakuman: '役满',
  };
  return names[r];
}

/**
 * 格式化支付
 * @param isDealer 是否亲家
 * @param method 和牌方式
 */
export function formatPayment(
  score: ScoreResult,
  isDealer: boolean,
  method: 'tsumo' | 'ron',
): string {
  const p = isDealer ? score.dealer : score.nonDealer;
  if (method === 'ron') {
    return `${p.ron} 点`;
  }
  if (isDealer) {
    // 亲家自摸：三家各付相同
    return `${p.tsumoDealer} 点 all（共 ${p.tsumoTotal}）`;
  }
  // 子家自摸：庄家付 X，闲家各付 Y
  return `${p.tsumoDealer} / ${p.tsumoNonDealer}（共 ${p.tsumoTotal}）`;
}

/** 点数摘要 */
export function summarizeScore(score: ScoreResult, isDealer: boolean): string {
  const parts: string[] = [];
  if (score.rank !== 'normal') parts.push(rankName(score.rank));
  else parts.push(`${score.han}番${score.fu}符`);
  if (score.honba > 0) parts.push(`${score.honba}本场`);
  return parts.join(' ');
}
